/**
 * Node Status System
 * Defines all possible statuses for mindmap nodes with their colors and metadata
 */

export type NodeStatus = 
  | 'backlog'
  | 'todo'
  | 'started'
  | 'in-review'
  | 'completed'
  | 'descoped'
  | 'cancelled';

export interface StatusConfig {
  value: NodeStatus;
  label: string;
  color: string;
  borderColor: string;
  backgroundColor: string;
  icon: string;
  description: string;
}

export const NODE_STATUSES: Record<NodeStatus, StatusConfig> = {
  backlog: {
    value: 'backlog',
    label: 'Backlog',
    color: '#8b92a1', // Gray - not done
    borderColor: '#8b92a1',
    backgroundColor: 'rgba(139, 146, 161, 0.1)',
    icon: 'inbox',
    description: 'In the backlog, not yet prioritized',
  },
  todo: {
    value: 'todo',
    label: 'To Do',
    color: '#8b92a1', // Gray - not done
    borderColor: '#8b92a1',
    backgroundColor: 'rgba(139, 146, 161, 0.1)',
    icon: 'circle',
    description: 'Ready to be worked on',
  },
  started: {
    value: 'started',
    label: 'Started',
    color: '#2196F3', // Blue - in progress
    borderColor: '#2196F3',
    backgroundColor: 'rgba(33, 150, 243, 0.1)',
    icon: 'play',
    description: 'Currently in progress',
  },
  'in-review': {
    value: 'in-review',
    label: 'In Review',
    color: '#9C27B0', // Purple - in review
    borderColor: '#9C27B0',
    backgroundColor: 'rgba(156, 39, 176, 0.1)',
    icon: 'eye-open',
    description: 'Under review or testing',
  },
  completed: {
    value: 'completed',
    label: 'Completed',
    color: '#4CAF50', // Green - done
    borderColor: '#4CAF50',
    backgroundColor: 'rgba(76, 175, 80, 0.1)',
    icon: 'tick-circle',
    description: 'Successfully completed',
  },
  descoped: {
    value: 'descoped',
    label: 'Descoped',
    color: '#9C27B0', // Purple
    borderColor: '#9C27B0',
    backgroundColor: 'rgba(156, 39, 176, 0.1)',
    icon: 'slash',
    description: 'Removed from scope',
  },
  cancelled: {
    value: 'cancelled',
    label: 'Cancelled',
    color: '#f44336', // Red
    borderColor: '#f44336',
    backgroundColor: 'rgba(244, 67, 54, 0.1)',
    icon: 'cross-circle',
    description: 'Cancelled and won\'t be done',
  },
};

// Helper to get status config by value
export const getStatusConfig = (status: NodeStatus | null | undefined): StatusConfig | null => {
  if (!status) return null;
  return NODE_STATUSES[status] || null;
};

// Get all statuses as an array (useful for dropdowns)
export const ALL_STATUSES: StatusConfig[] = Object.values(NODE_STATUSES);


